'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Lightbulb, X, ArrowLeft } from 'lucide-react';
import { PaymentsSettingsSidebar } from '@/components/dashboard/settings/payments/PaymentsSettingsSidebar';
import { MercadoPagoHeroCard } from '@/components/dashboard/settings/payments/MercadoPagoHeroCard';
import { PaymentMethodsGrid, PaymentMethodItem } from '@/components/dashboard/settings/payments/PaymentMethodsGrid';
import { BankTransferForm } from '@/components/dashboard/settings/payments/BankTransferForm';

const DEFAULT_METHODS: PaymentMethodItem[] = [
  { id: '1', type: 'EFECTIVO', name: 'Efectivo en mostrador', enabled: true },
  { id: '2', type: 'MERCADOPAGO', name: 'Mercado Pago (Seña Online)', enabled: true },
  { id: '3', type: 'TRANSFERENCIA', name: 'Transferencia Bancaria', enabled: true, bankAlias: 'juan.barber.mp', bankHolder: 'Juan Rojas', bankCbu: '0000003100012345678901' },
  { id: '4', type: 'DEBITO', name: 'Tarjeta de Débito (Posnet)', enabled: true },
  { id: '5', type: 'CREDITO', name: 'Tarjeta de Crédito (Posnet)', enabled: true },
  { id: '6', type: 'CUENTA_CORRIENTE', name: 'Cuenta Corriente / Fia', enabled: false },
  { id: '7', type: 'MODO', name: 'MODO / QR Bancario', enabled: true },
  { id: '8', type: 'OTRO', name: 'Otros medios', enabled: false }
];

export default function PaymentMethodsPage() {
  const [methods, setMethods] = useState<PaymentMethodItem[]>(DEFAULT_METHODS);
  const [mpConnected, setMpConnected] = useState(true);
  const [mpPublicKey, setMpPublicKey] = useState('APP_USR-78234-test-fake');
  const [mpAccessToken, setMpAccessToken] = useState('TEST-1234567890-test-fake');
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);
  const [bankAlias, setBankAlias] = useState('juan.barber.mp');
  const [bankHolder, setBankHolder] = useState('Juan Rojas');
  const [bankCbu, setBankCbu] = useState('0000003100012345678901');
  const [roundBalance, setRoundBalance] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchPaymentData = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch('/api/payment-methods', { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (data.success) {
        if (data.methods && data.methods.length > 0) {
          setMethods(data.methods);
        }
        if (data.mercadopago) {
          setMpConnected(data.mercadopago.connected ?? true);
          if (data.mercadopago.publicKey) setMpPublicKey(data.mercadopago.publicKey);
          if (data.mercadopago.accessToken) setMpAccessToken(data.mercadopago.accessToken);
          setHasWebhookSecret(Boolean(data.mercadopago.hasWebhookSecret));
        }

        // Obtener datos del método transferencia
        const transferMethod = (data.methods || []).find((m: any) => m.type === 'TRANSFERENCIA');
        if (transferMethod) {
          if (transferMethod.bankAlias) setBankAlias(transferMethod.bankAlias);
          if (transferMethod.bankHolder) setBankHolder(transferMethod.bankHolder);
          if (transferMethod.bankCbu) setBankCbu(transferMethod.bankCbu);
          if (transferMethod.roundBalance !== undefined) setRoundBalance(transferMethod.roundBalance);
        }
      }
    } catch (err) {
      console.warn('Syncing payments data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  // Toggle de un método de pago individual
  const handleToggleMethod = async (methodId: string, currentEnabled: boolean) => {
    try {
      const res = await fetch('/api/payment-methods', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ methodId, enabled: !currentEnabled })
      });
      const data = await res.json();
      if (data.success) {
        setMethods((prev) =>
          prev.map((m) => (m.id === methodId ? { ...m, enabled: !currentEnabled } : m))
        );
      }
    } catch (err) {
      alert('Error al actualizar método de pago');
    }
  };

  // Conectar / desconectar MP
  const handleToggleMpConnection = async () => {
    try {
      const res = await fetch('/api/payment-methods', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mercadopago: {
            connected: !mpConnected,
            publicKey: mpPublicKey,
            accessToken: mpAccessToken
          }
        })
      });
      const data = await res.json();
      if (data.success) {
        setMpConnected(!mpConnected);
        fetchPaymentData();
      }
    } catch (err) {
      alert('Error al modificar conexión');
    }
  };

  // Guardar credenciales API de MP
  const handleSaveMpCredentials = async (publicKey: string, accessToken: string, webhookSecret?: string) => {
    const res = await fetch('/api/payment-methods', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mercadopago: {
          connected: true,
          publicKey,
          accessToken,
          ...(webhookSecret ? { webhookSecret } : {})
        }
      })
    });
    const data = await res.json();
    if (data.success) {
      setMpPublicKey(publicKey);
      setMpAccessToken(accessToken);
      setMpConnected(true);
      if (webhookSecret) setHasWebhookSecret(true);
      fetchPaymentData();
    }
  };

  // Guardar datos bancarios
  const handleSaveBankDetails = async (details: {
    bankAlias: string;
    bankHolder: string;
    bankCbu: string;
    roundBalance: boolean;
  }) => {
    const transferMethod = methods.find((m) => m.type === 'TRANSFERENCIA');
    if (!transferMethod) return;

    const res = await fetch('/api/payment-methods', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        methodId: transferMethod.id,
        ...details
      })
    });
    const data = await res.json();
    if (data.success) {
      setBankAlias(details.bankAlias);
      setBankHolder(details.bankHolder);
      setBankCbu(details.bankCbu);
      setRoundBalance(details.roundBalance);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Top Banner Alert (Light Mint with bulb) */}
      {showBanner && (
        <div className="p-4 bg-emerald-50/80 border border-emerald-200/80 rounded-2xl flex items-start justify-between gap-3 text-xs text-emerald-950 shadow-2xs animate-in fade-in">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-lg bg-emerald-200/70 flex items-center justify-center shrink-0 mt-0.5 text-emerald-800">
              <Lightbulb className="w-3.5 h-3.5" />
            </div>
            <p className="leading-relaxed">
              <strong>Acá definís cómo cobrás.</strong> Activá los medios de pago que aceptás (efectivo, transferencia, MercadoPago) y, si querés, pedí una seña para confirmar los turnos y reducir ausencias.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowBanner(false)}
            className="p-1 text-emerald-800/60 hover:text-emerald-900 rounded-lg shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Breadcrumb & Title */}
      <div className="space-y-1">
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Configuración</span>
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          Métodos de pago
        </h1>
        <p className="text-xs text-slate-500">
          Cómo cobrás en tu negocio. Configurá una vez, usá en todos los turnos.
        </p>
      </div>

      {/* 2-Column Split: Sub-Sidebar on Left, MP Card + Grid on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-2">
        {/* Left Sub-Sidebar (TU NEGOCIO) */}
        <div className="lg:col-span-4">
          <PaymentsSettingsSidebar currentPath="/dashboard/settings/payment-methods" />
        </div>

        {/* Right Main Content */}
        <div className="lg:col-span-8 space-y-8">
          {/* Mercado Pago Hero Card */}
          <MercadoPagoHeroCard
            connected={mpConnected}
            publicKey={mpPublicKey}
            accessToken={mpAccessToken}
            hasWebhookSecret={hasWebhookSecret}
            onToggleConnection={handleToggleMpConnection}
            onSaveCredentials={handleSaveMpCredentials}
          />

          {/* Grid: 8 Payment Methods on Left, Bank Transfer Form on Right */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
            <div className="xl:col-span-7">
              <PaymentMethodsGrid
                methods={methods}
                mpConnected={mpConnected}
                onToggleMethod={handleToggleMethod}
              />
            </div>

            <div className="xl:col-span-5">
              <BankTransferForm
                initialAlias={bankAlias}
                initialHolder={bankHolder}
                initialCbu={bankCbu}
                initialRoundBalance={roundBalance}
                onSaveBankDetails={handleSaveBankDetails}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
